import { Component, inject, OnInit, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';

import { UserProfileStore } from '../store/user-profile.store';
import {
  UpdateProfileDTO,
  FormErrors,
} from '../../../types/user-profile.types';
import { TextInputComponent, SubmitButtonComponent } from '../../forms';
import { ErrorAlertComponent, SuccessAlertComponent } from '../../alerts';

// Simple timezone and locale options for the form
const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'Europe/London', label: 'GMT/BST' },
  { value: 'Europe/Paris', label: 'CET/CEST' },
  { value: 'Asia/Tokyo', label: 'JST' },
];

const LOCALE_OPTIONS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'ja-JP', label: 'Japanese' },
];

@Component({
  standalone: true,
  imports: [
    FormsModule,
    TextInputComponent,
    SubmitButtonComponent,
    ErrorAlertComponent,
    SuccessAlertComponent
],
  selector: 'sb-profile-form',
  templateUrl: './profile-form.component.html',
})
export class ProfileFormComponent implements OnInit {
  // @ts-ignore TS4029
  public userProfileStore = inject(UserProfileStore);

  // Form data
  formData = signal<UpdateProfileDTO>({
    first_name: '',
    last_name: '',
    username: '',
    locale: 'en-US',
    timezone: 'UTC',
  });

  // Form errors
  formErrors = signal<FormErrors>({});

  // Success message
  successMessage = signal<string | null>(null);

  // Make timezone and locale options available to template
  readonly timezoneOptions = TIMEZONE_OPTIONS;
  readonly localeOptions = LOCALE_OPTIONS;

  ngOnInit() {
    // Load user data from store and populate form
    const user = this.userProfileStore.user();
    if (user) {
      this.formData.set({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        username: user.username || '',
        locale: user.locale || 'en-US',
        timezone: user.timezone || 'UTC',
      });
    }
  }

  validateField(fieldName: string) {
    const value = this.formData()[fieldName as keyof UpdateProfileDTO];
    const errors = { ...this.formErrors() };

    // Clear previous error
    delete errors[fieldName];

    // Validate required fields
    if (
      (fieldName === 'first_name' || fieldName === 'last_name') &&
      !value?.trim()
    ) {
      errors[fieldName] = `${fieldName.replace('_', ' ')} is required`;
    }

    // Validate username format if provided
    if (fieldName === 'username' && value && value.trim()) {
      const usernameRegex = /^[a-zA-Z0-9_]+$/;
      if (!usernameRegex.test(value.trim())) {
        errors[fieldName] =
          'Username can only contain letters, numbers, and underscores';
      }
    }

    this.formErrors.set(errors);
  }

  updateFormField(field: keyof UpdateProfileDTO, value: string) {
    this.formData.update((data) => ({
      ...data,
      [field]: value,
    }));

    // Clear any existing error for this field
    this.formErrors.update((errors) => {
      const newErrors = { ...errors };
      delete newErrors[field];
      return newErrors;
    });
  }

  isFormValid(): boolean {
    const data = this.formData();
    const errors = this.formErrors();

    // Check if required fields are filled
    const hasRequiredFields = !!(
      data.first_name?.trim() && data.last_name?.trim()
    );

    // Check if there are no validation errors
    const hasNoErrors = Object.keys(errors).length === 0;

    return hasRequiredFields && hasNoErrors;
  }

  async onSubmit() {
    // Validate all fields
    this.validateField('first_name');
    this.validateField('last_name');
    this.validateField('username');

    if (!this.isFormValid()) {
      return;
    }

    this.successMessage.set(null);

    try {
      await this.userProfileStore.updateProfile(this.formData());
      this.successMessage.set('Profile updated successfully!');

      // Clear success message after 5 seconds
      setTimeout(() => {
        this.successMessage.set(null);
      }, 5000);
    } catch (error) {
      // Error is handled by the store
    }
  }

  resetForm() {
    const user = this.userProfileStore.user();
    if (user) {
      this.formData.set({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        username: user.username || '',
        locale: user.locale || 'en-US',
        timezone: user.timezone || 'UTC',
      });
    }
    this.formErrors.set({});
    this.successMessage.set(null);
  }
}
